import React, { useEffect, useState } from "react";
import { FaCoins, FaSave } from "react-icons/fa";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import { useGetOkkaliraRateQuery, useUpdateOkkaliraRateMutation } from "../../store";
import { MascotLoadingOverlay } from "../shared";
import NumberInput from "../atoms/NumberInput";
import { formatDate } from "./walletTxUi";
import { formatKurus, okkaliraRateError } from "../../utils/enduserWallet";

/**
 * Organizma: Okkalira kuru ayarı (#400) — 1 Okkalira = X kuruş. Uygulamada
 * "Okkalira kullan" seçilen QR ödemelerde bu kurla harcanır.
 * Sözleşme: Workspace docs/api-contracts/okkalira-spend.md §1
 */
export default function OkkaliraRateCard() {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const { data, isLoading } = useGetOkkaliraRateQuery(
    { token: auth?.accessToken },
    { skip: !auth?.accessToken }
  );
  const [updateRate, { isLoading: saving }] = useUpdateOkkaliraRateMutation();
  const info = data?.data;
  const [rate, setRate] = useState("");

  useEffect(() => {
    if (info?.rateKurus != null) setRate(info.rateKurus);
  }, [info?.rateKurus]);

  const error = okkaliraRateError(rate);
  const changed = info && Number(rate) !== info.rateKurus;

  const handleSave = async () => {
    if (error) return;
    try {
      await updateRate({ token: auth.accessToken, rateKurus: Number(rate) }).unwrap();
      showSuccess("Okkalira kuru güncellendi.");
    } catch (err) {
      showError(err?.data?.message || "Okkalira kuru güncellenemedi.");
    }
  };

  if (isLoading) {
    return (
      <div className="relative">
        <MascotLoadingOverlay message="Okkalira kuru yükleniyor..." />
        <div className="h-48 rounded-xl bg-gray-100 motion-safe:animate-pulse" />
      </div>
    );
  }

  const by = info?.updatedBy ? [info.updatedBy.name, info.updatedBy.lastName].filter(Boolean).join(" ") : null;

  return (
    <div className="max-w-xl rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-50/80 border-b border-gray-100">
        <div className="bg-amber-500 text-white p-1.5 rounded-lg shadow-sm">
          <FaCoins className="w-4 h-4" />
        </div>
        <span className="text-sm font-SemiBold text-gray-800">Okkalira Kuru</span>
      </div>
      <div className="p-4 space-y-4">
        <NumberInput
          label="1 Okkalira kaç kuruş?"
          required
          min={1}
          step={1}
          value={rate}
          onChange={setRate}
          suffix="kuruş"
          error={!!error}
          errorMessage={error || ""}
          aria-label="Okkalira kuru"
        />
        {!error && (
          <p className="text-sm text-gray-700">
            1 Okkalira = <span className="font-SemiBold">{formatKurus(rate)}</span> · 100 Okkalira ={" "}
            <span className="font-SemiBold">{formatKurus(Number(rate) * 100)}</span>
          </p>
        )}
        <p className="text-xs text-gray-500">
          Kullanıcı uygulamada "Okkalira kullan" derse QR ödemede bu kurla harcanır. Kur değişikliği
          yapılmış ödemelerin iadesini etkilemez; iade ödeme anındaki kurla yapılır.
        </p>
        {info?.updatedAt && (
          <p className="text-[11px] text-gray-400">
            Son değişiklik: {formatDate(info.updatedAt)}
            {by ? ` · ${by}` : ""}
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !!error || !changed}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50"
          >
            <FaSave className="w-3.5 h-3.5" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}
